import { Button, Form, InputGroup, Card, Container } from '@themesberg/react-bootstrap'
import { ErrorMessage, Formik } from 'formik'
import React, { useMemo } from 'react'
import { depositSchema } from './schema'
import useRolloverBonus from './hook/useRolloverBonus'
import { formatDate, convertDateRangeToEastern } from '../../../utils/dateFormatter'
import DateRangePicker from '../../../components/DateRangePicker'
import { Col, Row, Spinner, Badge } from 'react-bootstrap'
import Preloader from '../../../components/Preloader'
import NotificationIconContainer from '../../../components/NotificationIcon/NotificationIconContainer'

const RolloverBonus = () => {
  const {
    deposit,
    state,
    setState,
    loading,
    isFetching,
    rollOverBonusDetail
  } = useRolloverBonus()

  const initialValues = useMemo(() => ({
    bonusAmount: rollOverBonusDetail?.bonusAmount ?? '',
    startDate: formatDate(rollOverBonusDetail?.createdAt ?? state?.[0]?.startDate),
    endDate: formatDate(rollOverBonusDetail?.updatedAt ?? state?.[0]?.endDate)
  }), [rollOverBonusDetail, state])

  const hasValuesChanged = (currentValues, initialValues) => {
    return (
      currentValues.bonusAmount !== initialValues.bonusAmount ||
      currentValues.startDate !== initialValues.startDate ||
      currentValues.endDate !== initialValues.endDate
    )
  }

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'secondary'
      case 'expired':
        return 'danger'
      default:
        return 'warning'
    }
  }

  const handleFormSubmit = (formValues) => {
    console.log('Original form values: ', formValues)

    // Convert dates to Eastern timezone
    const easternDates = convertDateRangeToEastern(formValues.startDate, formValues.endDate)

    const apiPayload = {
      bonusAmount: formValues.bonusAmount,
      startDate: easternDates.startDate,
      endDate: easternDates.endDate
    }

    console.log('API payload with Eastern dates: ', apiPayload)
    console.log('Start Date (Eastern):', easternDates.startDate)
    console.log('End Date (Eastern):', easternDates.endDate)

    // Send the converted dates to the API
    deposit(apiPayload)
  }

  return (
    <div  className="">
      {isFetching ? (
        <Preloader />
      ) : (
        <>

          <div className='section-header-wrap mt-0 mx-n3 mb-4'>
            <Row className='align-items-center justify-content-between' >
              <Col>
                <h2 className='title-text'> Bonus</h2>
              </Col>
              <Col xs={2} className="text-end">
                <NotificationIconContainer />
              </Col>
            </Row>
          </div>

          <Card.Header className="bg-white border-bottom">
            <Row className="align-items-center">
              <Col className="d-flex align-items-center justify-content-between">
                <div className="d-flex flex-column flex-sm-row align-items-sm-center">
                  <h4 className="mb-2 mb-sm-0 me-sm-3">Create Rollover Bonus</h4>
                  <div className="d-flex align-items-center">
                    <span className="text-muted me-2">Status:</span>
                    <Badge
                      bg={getStatusVariant(rollOverBonusDetail?.status)}
                      className="text-uppercase"
                    >
                      {rollOverBonusDetail?.status || 'Not Set'}
                    </Badge>
                  </div>
                </div>

              </Col>
            </Row>
          </Card.Header>

          <Row className="justify-content-center">
            <Col xs={12} >
              <Card className="shadow-sm border-0">


                <Card.Body className="p-4">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={depositSchema}
                    onSubmit={(formValues, { resetForm }) => {
                      handleFormSubmit(formValues)
                      // resetForm()
                    }}
                    enableReinitialize
                  >
                    {({ values, touched, errors, handleChange, handleBlur, handleSubmit, resetForm, setFieldValue }) => {
                      const isFormUnchanged = !hasValuesChanged(values, initialValues)

                      return (
                        <Form>
                          <Row>
                            <Col xs={12}>
                              <Form.Group className="mb-4">
                                <Form.Label
                                  htmlFor="bonusAmount"
                                  className={`fw-bold ${touched.bonusAmount && errors.bonusAmount ? 'text-danger' : 'text-dark'}`}
                                >
                                  Bonus Amount <span className="text-danger">*</span>
                                </Form.Label>
                                <InputGroup
                                  className={touched.bonusAmount && errors.bonusAmount ? 'border border-danger rounded' : ''}
                                >
                                  <InputGroup.Text>$</InputGroup.Text>
                                  <Form.Control
                                    name="bonusAmount"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="Enter bonus amount"
                                    value={values.bonusAmount}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className={touched.bonusAmount && errors.bonusAmount ? 'border-danger' : ''}
                                  // disabled={rollOverBonusDetail?.status === 'ACTIVE'}
                                  />
                                </InputGroup>
                                <ErrorMessage component="div" name="bonusAmount" className="text-danger small mt-1" />
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col xs={12}>
                              <Form.Group className="mb-4">
                                <Form.Label
                                  className={`fw-bold ${touched.bonusType && errors.bonusType ? 'text-danger' : 'text-dark'}`}
                                >
                                  Bonus Validity Period <span className="text-danger">*</span>
                                </Form.Label>
                                <InputGroup
                                  className={touched.bonusType && errors.bonusType ? 'border border-danger rounded' : ''}
                                >
                                  <DateRangePicker
                                    width="100%"
                                    state={state}
                                    setState={(newState) => {
                                      setState(newState);
                                      setFieldValue('startDate', formatDate(newState?.[0]?.startDate));
                                      setFieldValue('endDate', formatDate(newState?.[0]?.endDate));
                                    }}
                                    size="md"
                                    bonus
                                  // disabled={rollOverBonusDetail?.status === 'ACTIVE'}
                                  />
                                </InputGroup>
                                <small className="text-muted">
                                  Select the start and end date for the bonus validity
                                </small>
                              </Form.Group>
                            </Col>
                          </Row>

                          {/* Display selected dates */}
                          {(values.startDate || values.endDate) && (
                            <Row className="mb-4">
                              <Col xs={12}>
                                <Card className="bg-light border-0">
                                  <Card.Body className="p-3">
                                    <h6 className="mb-2 text-muted">Selected Period:</h6>
                                    <Row>
                                      <Col xs={12} sm={6}>
                                        <small className="text-muted">Start Date:</small>
                                        <div className="fw-bold">{values.startDate || 'Not selected'}</div>
                                      </Col>
                                      <Col xs={12} sm={6} className="mt-2 mt-sm-0">
                                        <small className="text-muted">End Date:</small>
                                        <div className="fw-bold">{values.endDate || 'Not selected'}</div>
                                      </Col>
                                    </Row>
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          )}

                          <Row>
                            <Col xs={12}>
                              <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end">
                                <Button
                                  variant="outline-secondary"
                                  onClick={() => {
                                    resetForm();
                                    setState([
                                      {
                                        startDate: new Date(),
                                        endDate: new Date(),
                                        key: 'selection',
                                      },
                                    ])

                                    setFieldValue('startDate', formatDate(new Date()));
                                    setFieldValue('endDate', formatDate(new Date()));
                                  }}
                                  disabled={loading}
                                  className="order-2 order-sm-1"
                                >
                                  Reset Form
                                </Button>
                                <Button
                                  variant="success"
                                  onClick={handleSubmit}
                                  disabled={loading || isFormUnchanged}
                                  className="order-1 order-sm-2 d-flex align-items-center justify-content-center"
                                >
                                  {loading && (
                                    <Spinner
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-2"
                                    />
                                  )}
                                  {loading ? 'Processing...' : 'Create Bonus'}
                                </Button>
                              </div>
                            </Col>
                          </Row>
                        </Form>
                      )
                    }}
                  </Formik>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>

      )}
    </div>
  )
}

export default RolloverBonus
